import ConfigProvider from 'antd/es/config-provider';
import React, { createContext, useCallback, useContext } from 'react';

import GlobalStyle, { type ThemeMode, createCustomTheme } from './GlobalStyle';

import { usePersistentState } from '@utils/hooks';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  themeToken: ReturnType<typeof createCustomTheme>['token'];
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  themeToken: createCustomTheme('light').token,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = usePersistentState<ThemeMode>('theme', 'light');
  const customTheme = createCustomTheme(theme);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  return (
    <>
      <GlobalStyle />
      <ThemeContext.Provider
        value={{ theme, toggleTheme, themeToken: customTheme.token }}
      >
        <ConfigProvider theme={customTheme}>{children}</ConfigProvider>
      </ThemeContext.Provider>
    </>
  );
};
